import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Skeleton, message } from 'antd';
import type { UploadFile } from 'antd';
import { useParams } from 'react-router-dom';
import { Gallery } from 'iconsax-react';
import TitleText from '../../../../atoms/TitleText';
import InfoText from '../../../../atoms/InfoText';
import Button from '../../../../atoms/Button';
import FormField from '../../../../FormField/FormField';
import { openModal } from '../../../../../store/slices/Modals';
import {
  useGetProfileInfoQuery,
  useGetProfilePhotoQuery,
  useUploadAzureMutation,
  useAddSliderImagesMutation,
  useGetSliderImagesQuery,
  useCreateMarketplaceMutation,
} from '../../../../../store/services/sellerProfileCreationService';
import SuccessMarketplaceModal from './SuccessMarketplaceModal';

const CreateMarketForm = ({
  createMarketplace,
}: {
  createMarketplace: ReturnType<typeof useCreateMarketplaceMutation>[0];
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sellerId } = useParams<{ sellerId: string }>();
  const profileId = sellerId || '';

  const schema = z.object({
    profilePhoto: z.custom<File | string | null>().optional(),
    storeName: z.string({ message: t('createMarket.requiredField', 'This field cannot be empty') }).trim().min(1, { message: t('createMarket.requiredField', 'This field cannot be empty') }),
    sliderImages: z.array(z.custom<UploadFile>()).optional(),
  });

  type CreateMarketFormData = z.infer<typeof schema>;

  const { data: companyInfoData, isLoading: loadingCompanyInfo } = useGetProfileInfoQuery({ profileId }, { skip: !profileId });
  const { data: profilePhotoData } = useGetProfilePhotoQuery({ profileId }, { skip: !profileId });
  const { data: sliderImagesData } = useGetSliderImagesQuery({ profileId }, { skip: !profileId });
  const [uploadToAzure, { isLoading: uploadingToAzure }] = useUploadAzureMutation();
  const [uploadSliderImages] = useAddSliderImagesMutation();

  const [uploadedProfilePhotoId, setUploadedProfilePhotoId] = useState<string | undefined>();
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);

  console.log("🚀 ~ CreateMarketForm ~ profilePhotoData?.payload?.blobFileUrl:", profilePhotoData?.payload?.blobFileUrl)
  const methods = useForm<CreateMarketFormData>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      profilePhoto: null,
      storeName: '',
      sliderImages: [],
    },
    values: {
      profilePhoto: profilePhotoData?.payload?.blobFileUrl as string,
      storeName: companyInfoData?.payload?.name ?? '',
      sliderImages: sliderImagesData?.payload?.map(doc => ({
        uid: doc.id,
        name: doc.name || 'image',
        status: 'done' as const,
        url: doc.blobFileUrl,
        response: { payload: [doc] }
      })) ?? [],
    },
    resetOptions: { keepDirtyValues: true }
  });

  const uploadRef = useRef<HTMLInputElement>(null);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPreviewPhotoUrl(URL.createObjectURL(file));
    methods.setValue('profilePhoto', file);

    const formData = new FormData();
    formData.append('files', file);

    try {
      const response = await uploadToAzure(formData).unwrap();
      if (response?.payload?.[0]) {
        setUploadedProfilePhotoId(response.payload[0].id);
      }
    } catch (err: unknown) {
      console.error(err);
      const errorMsg = (err as { data?: { message?: string } })?.data?.message || t('components.somethingWentWrong', 'Something went wrong');
      message.error(errorMsg);
    }
  };

  const handleSliderImagesChange = async (files: UploadFile[]) => {
    if (!files || files.length === 0) return [];

    const existingIds = files
      .filter((file) => !file.originFileObj && file.uid)
      .map((file) => file.uid);

    const newFiles = files.filter((file) => file.originFileObj);

    if (newFiles.length === 0) return existingIds;

    const formData = new FormData();
    newFiles.forEach((file) => {
      if (file.originFileObj) {
        formData.append('files', file.originFileObj);
      }
    });

    const response = await uploadSliderImages({ profileId, body: formData }).unwrap();
    const newIds = response?.payload?.map((img) => img.id) ?? [];

    return [...existingIds, ...newIds];
  };

  const onSubmit = async (data: CreateMarketFormData) => {
    const sliderIds =
      data.sliderImages && data.sliderImages.length > 0
        ? await handleSliderImagesChange(data.sliderImages)
        : [];

    const photoId = uploadedProfilePhotoId || profilePhotoData?.payload?.id

    try {
      await createMarketplace({
        profileId,
        data: {
          marketplaceName: data.storeName,
          sliderImagesIds: sliderIds ?? [],
          ...(photoId ? { profilePhotoId: photoId } : {}),
        }
      }).unwrap();

      dispatch(openModal({ id: 'SUCCESS_MARKETPLACE_MODAL' }));
    } catch (err: unknown) {
      console.error(err);
      const errorMsg = (err as { data?: { message?: string } })?.data?.message || t('components.somethingWentWrong', 'Something went wrong');
      message.error(errorMsg);
    }
  };

  const currentPhotoSrc = previewPhotoUrl || companyInfoData?.payload?.photoUrl;

  return (
    <>
      <FormProvider {...methods}>
        <form
          id="create-marketplace-form"
          onSubmit={methods.handleSubmit(onSubmit)}
          className="w-full bg-white dark:bg-dark-surface rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-dark-border shadow-sm flex flex-col font-sans"
          data-testid="create-market-form"
        >
          {/* Main Title Section */}
          <div className="flex flex-col gap-1 mb-6" data-testid="create-market-header">
            <TitleText
              text={t('createMarket.letsGetStarted', "Let's get started")}
              className="text-[24px] font-bold text-[#090909] dark:text-white leading-tight"
              data-testid="create-market-title"
            />
            <InfoText
              text={t('createMarket.addDetails', 'Add some details to customize your setup')}
              className="text-sm font-normal text-[#777777] dark:text-gray-400 mt-0.5"
              data-testid="create-market-header-desc"
            />
          </div>

          {/* Store Information Subsection */}
          <div className="flex flex-col gap-1 mb-4" data-testid="create-market-store-info-header">
            <TitleText
              text={t('createMarket.storeInfo', 'Store information')}
              className="text-[18px] font-bold text-[#090909] dark:text-white leading-tight"
              data-testid="create-market-store-info-title"
            />
            <InfoText
              text={t('createMarket.addBasicDetails', 'Add your basic details so buyers can trust and contact your business.')}
              className="text-sm font-normal text-[#666666] dark:text-gray-400 mt-0.5"
              data-testid="create-market-store-info-desc"
            />
          </div>

          {/* Profile Photo Upload Section */}
          <div className="flex flex-col gap-4 mb-6" data-testid="create-market-profile-photo-section">
            {loadingCompanyInfo ? (
              <Skeleton.Avatar active size={140} data-testid="create-market-profile-photo-skeleton" />
            ) : (
              <div className="flex flex-col sm:flex-row items-start gap-6" data-testid="create-market-profile-photo-controls">
                {/* 143x140px Circular Photo Frame */}
                <div
                  onClick={() => uploadRef.current?.click()}
                  className="w-[143px] h-[140px] rounded-full border border-dashed border-[#acacac] bg-white dark:bg-dark-surface flex items-center justify-center shrink-0 cursor-pointer overflow-hidden hover:border-pink-500 transition-colors shadow-xs"
                  data-testid="profile-photo-circle"
                >
                  {currentPhotoSrc ? (
                    <img src={currentPhotoSrc} alt="Store Profile" className="w-full h-full object-cover" />
                  ) : (
                    <Gallery size={24} className="text-[#acacac]" variant="Bold" />
                  )}
                </div>

                <input
                  ref={uploadRef}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  className="hidden"
                  onChange={handlePhotoSelect}
                  data-testid="profile-photo-input-file"
                />

                <div className="flex flex-col gap-2 max-w-[400px]" data-testid="create-market-profile-photo-texts">
                  <span className="text-[16px] font-semibold text-[#090909] dark:text-white">
                    {t('createMarket.profilePhoto', 'Profile photo')}
                  </span>
                  <span className="text-sm font-normal text-[#666666] dark:text-gray-400">
                    {t(
                      'createMarket.choosePhoto',
                      'Choose a photo to upload. Supported formats: JPG, PNG, JPEG. Maximum size: 5MB.'
                    )}
                  </span>
                  <div className="mt-0" data-testid="create-market-profile-photo-btn">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => uploadRef.current?.click()}
                      disabled={uploadingToAzure}
                      className="min-w-[140px] h-[46px] rounded-[12px] whitespace-nowrap px-4 font-semibold text-[16px] transition-all"
                      data-testid="create-market-btn-edit-photo"
                    >
                      {t('createMarket.editPhoto', 'Edit photo')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Store Name Input */}
          <div className="flex flex-col mb-8" data-testid="create-market-input-store-name-container">
            <label className="text-[16px] font-semibold text-[#000000] dark:text-gray-200 block mb-1.5">
              {t('createMarket.storeName', 'Your store name')}
            </label>
            <FormField
              name="storeName"
              id="create-market-input-store-name"
              type="text"
              placeholder={t('createMarket.storeNamePlaceholder', 'Visa cards co.')}
              className="h-[50px] w-full rounded-xl bg-transparent dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 text-sm font-normal text-[#090909] dark:text-white placeholder:text-[#acacac] placeholder:font-normal hover:border-gray-300 focus:border-gray-300 transition-colors"
              data-testid="create-market-input-store-name"
            />
          </div>

          {/* Advertise Slider Images Section */}
          <div
            className="flex flex-col gap-2"
            data-testid="create-market-slider-section"
          >
            <div
              className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2"
              data-testid="create-market-slider-header"
            >
              <div
                className="flex flex-col gap-1"
                data-testid="create-market-slider-titles"
              >
                <TitleText
                  text={t('sliderImages', 'Advertise slider images')}
                  size="base"
                  className="!font-semibold !text-[16px] !leading-[100%] text-[#090909] dark:text-white"
                  data-testid="create-market-slider-title"
                />
                <InfoText text={t('sliderImagesSubtitle', 'This slider will display in your marketplace profile')} size="sm" className="dark:text-dark-text-secondary" data-testid="create-market-slider-desc" />
              </div>
              <InfoText
                text={t('sliderImagesCount', '{{count}}/5 image', {
                  count: useWatch({ control: methods.control, name: 'sliderImages' })?.length ?? 0,
                })}
                size="sm"
                className="dark:text-dark-text-secondary"
                data-testid="create-market-slider-count"
              />
            </div>
            <FormField
              type="file"
              id="slider-images"
              name="sliderImages"
              maxCount={5}
              maxFileSize={10}
              allowedTypes={['image/jpeg', 'image/png', 'image/jpg']}
              uploadHints={[t('acceptedFilesType'), t('acceptedFilesLimit')]}
              data-testid="create-market-uploader-slider-images"
            />
          </div>
        </form>
      </FormProvider>
      <SuccessMarketplaceModal />
    </>
  );
};

export default CreateMarketForm;
